import {useEffect, useState} from "react";
import {NavigationType, useLocation, useNavigationType, useSearchParams} from "react-router";
import {useDebouncedCallback} from "use-debounce";

export const DEFAULT_SEARCH_KEY = "search";
export const DEFAULT_DEBOUNCE_DELAY = 500;

export interface UseDebouncedSearchOptions {
  searchKey?: string;
  debounceDelay?: number;
  onSearchChange?: (search: string, params: URLSearchParams) => void;
  useUrlParams?: boolean;
  clearKeysOnSearchChange?: string[];
}

export const useDebouncedSearch = (options: UseDebouncedSearchOptions = {}) => {
  const {
    searchKey = DEFAULT_SEARCH_KEY,
    debounceDelay = DEFAULT_DEBOUNCE_DELAY,
    onSearchChange,
    useUrlParams = true,
    clearKeysOnSearchChange,
  } = options;

  const [queryParams, setQueryParams] = useSearchParams();
  const {key: locationKey} = useLocation();
  const navigationType = useNavigationType();
  const urlSearchValue = useUrlParams ? queryParams.get(searchKey) || "" : "";
  const [search, setSearch] = useState<string>(urlSearchValue);
  const [searchDebounced, setSearchDebounced] = useState<string>(search);

  const searchChangedDebounced = useDebouncedCallback((newSearch: string) => {
    if (useUrlParams) {
      const params = new URLSearchParams(queryParams);
      if (newSearch.length === 0) {
        params.delete(searchKey);
      } else {
        params.set(searchKey, newSearch);
      }
      if (clearKeysOnSearchChange) {
        clearKeysOnSearchChange.forEach((key) => {
          params.delete(key);
        });
      }
      onSearchChange?.(newSearch, params);
      setSearchDebounced(newSearch);
      setQueryParams(params);
    } else {
      onSearchChange?.(newSearch, new URLSearchParams());
      setSearchDebounced(newSearch);
    }
  }, debounceDelay);

  // Compared with the last written search, not the input: the URL catching up to this hook's own write must not undo
  // keys typed since. Back, Forward and any other change of the search drop a write still waiting, so it cannot undo
  // them.
  useEffect(() => {
    if (!useUrlParams) {
      return;
    }
    if (navigationType === NavigationType.Pop || searchDebounced !== urlSearchValue) {
      searchChangedDebounced.cancel();
      setSearch(urlSearchValue);
      setSearchDebounced(urlSearchValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- search state intentionally excluded to prevent infinite loops; this effect syncs URL → state
  }, [urlSearchValue, useUrlParams, locationKey]);

  const searchChanged = (newSearch: string) => {
    setSearch(newSearch);
    searchChangedDebounced(newSearch);
  };

  // For a switch to another list that drops the search itself: a write still waiting would carry the keys typed for
  // this list over to the next one.
  const discardPendingSearch = () => {
    searchChangedDebounced.cancel();
    setSearch(searchDebounced);
  };

  return {
    search,
    searchDebounced,
    searchChanged,
    discardPendingSearch,
    setSearch,
    setSearchDebounced,
  };
};
